import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';

import { LogoInnovamat } from '@innovamat/glimmer-assets';
import {
  AnnouncementPanel,
  Button,
  Input,
  InputPhone,
  Select,
  Typography,
} from '@innovamat/glimmer-components';
import { validators } from '@innovamat/radiance-utils';

import { SuccessMessage } from '../../components/success-message';
import { useGetUserRegion } from '../../hooks/use-user-region';
import { ImageSlider } from './components/image-slider';
import { LanguageSelector } from './components/language-selector';
import { useLeadRegister } from './hooks/use-lead-register';

const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-primary'].value};
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-primary'].value};
  position: sticky;
  top: 0;
  z-index: 10;
  margin: 16px 16px 32px 16px;
  flex-shrink: 0;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    height: 32px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-self: center;
  padding: 0 16px 32px 16px;
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled(Typography.H2)`
  font-weight: 700;
`;

const ThinkingMath = styled.span`
  --w: 8px;
  --h: 2.5px;
  --c: #f15525;
  position: relative;
  display: inline-block;
  background: bottom / calc(2 * var(--w)) calc(2 * var(--h)) space no-repeat;
  --_g: calc(var(--w) / 2) top 50%, var(--c) 90%, #0000 105%;
  background-image: radial-gradient(var(--h) at left var(--_g)),
    radial-gradient(var(--h) at right var(--_g)),
    linear-gradient(
      90deg,
      #0000 calc(var(--w) / 2),
      var(--c) 0 calc(3 * var(--w) / 2),
      #0000 0
    );
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(var(--h) * 2);
    background: white;
    animation: slideReveal 0.5s ease-in-out forwards;
    animation-delay: 0.2s;
  }

  @keyframes slideReveal {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @media (max-width: 768px) {
    --w: 6px;
    --h: 2px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorText = styled(Typography.Body2)`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text['text-error'].value};
  text-align: center;
  margin: 0;
`;

function LeadRegister(): JSX.Element {
  const { t } = useTranslation();
  const urlRegion = useGetUserRegion();

  const {
    user,
    phoneDialCode,
    langOptions,
    isPending,
    isSuccess,
    isError,
    changeInput,
    handleChangePhone,
    handleRegisterUser,
    validateInputs,
    getError,
  } = useLeadRegister();

  const { firstName, lastName, email, phoneNumber, preferences } = user;

  if (isSuccess) {
    return (
      <PageContainer>
        <ContentContainer>
          <Header>
            <LogoWrapper>
              <LogoInnovamat />
            </LogoWrapper>
            <LanguageSelector />
          </Header>
          <FormCard>
            <SuccessMessage
              title={t(
                'Hemos mandado un correo a la dirección indicada en el que encontrarás un enlace para generar tu contraseña y finalizar el registro.'
              )}
              body={
                <>
                  <span>
                    {t('Si no lo recibes revisa la carpeta de spam.')}{' '}
                  </span>
                  <span>
                    {t('Muchas gracias.')}
                    <br />
                    {t('El equipo Innovamat.')}
                  </span>
                </>
              }
            />
          </FormCard>
        </ContentContainer>
        <ImageSlider />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <LogoWrapper>
            <LogoInnovamat />
          </LogoWrapper>
          <LanguageSelector />
        </Header>
        <FormCard>
          <FormHeader>
            <Title>
              <Trans
                i18nKey="LeadRegister.page.title"
                components={{ dots: <ThinkingMath />, br: <br /> }}
              />
            </Title>
          </FormHeader>

          {isError && (
            <AnnouncementPanel
              type="error"
              text={getError()}
              canClose={false}
            />
          )}

          <Form onSubmit={handleRegisterUser}>
            <Input
              labelText={t('Settings.Name')}
              placeholder="ex. John"
              value={firstName}
              onChange={(e) => changeInput(e.target.value, 'firstName')}
              dataTestId="lead-register-name"
              fill
            />

            <Input
              labelText={t('Settings.Surname')}
              placeholder="ex. Doe"
              value={lastName}
              onChange={(e) => changeInput(e.target.value, 'lastName')}
              dataTestId="lead-register-surname"
              fill
            />

            <Input
              labelText={t('Settings.Email')}
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => changeInput(e.target.value, 'email')}
              status={
                email && !validators.isEmailValid(email) ? 'error' : undefined
              }
              descriptiveText={
                email && !validators.isEmailValid(email)
                  ? t('LeadRegister.email.error')
                  : undefined
              }
              dataTestId="lead-register-email"
              fill
            />

            <InputPhone
              country={urlRegion.toLocaleLowerCase()}
              label={t('teacher.onboarding.createProfileFilled.field.phone')}
              phone={phoneNumber || ''}
              onChange={handleChangePhone}
              error={!validators.isPhoneValid(phoneNumber, phoneDialCode)}
              description={
                !validators.isPhoneValid(phoneNumber, phoneDialCode)
                  ? t(
                      'teacher.onboarding.createProfileFilled.phoneNumber.error'
                    )
                  : t(
                      'teacher.onboarding.createProfileFilled.phoneNumber.tagline'
                    )
              }
              optionalText={t('input.optional')}
              data-testid="lead-register-phone"
            />

            <Select
              value={preferences.language}
              options={langOptions}
              labelText={t(
                'teacher.onboarding.createProfileFilled.field.platformLanguage'
              )}
              onChange={(opt) => {
                if (opt?.value) {
                  changeInput(opt.value, 'preferences', 'language');
                }
              }}
              dataTestId="lead-register-language"
              menuPlacement="auto"
            />

            <Button
              fill
              variant="accent"
              type="submit"
              disabled={!validateInputs()}
              loading={isPending}
              dataTestId="lead-register-submit"
            >
              {t('LandingRegister.Enviar')}
            </Button>
          </Form>
        </FormCard>
      </ContentContainer>
      <ImageSlider />
    </PageContainer>
  );
}

export { LeadRegister };
